import lodash from 'lodash';
import { DateTime } from 'luxon';

import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewContainerRef,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { CarouselModule } from 'primeng/carousel';
import { ChartModule } from 'primeng/chart';

import { COLORS } from '@styles/colors';

import { SummaryChartComponent } from '@cxc/ui-kit/chart.js/components/summary-chart';
import { ChartColorScheme } from '@cxc/ui-kit/chart.js/const-enums';
import { decodeChart } from '@cxc/ui-kit/chart.js/decode';
import { Chart, SummaryChart } from '@cxc/ui-kit/chart.js/models';

import { SelectableWidget } from '@components/selectable-widget/selectable-widget.component';

import { CURRENCY } from '@cxc/core/enums';

import { API } from '@models/API';

import { AnalyticsLabelValuePipe } from '@cxc/ui-kit/pipes';

import { OverviewResumeState } from '@services/state/overview-resume-state/overview-resume.state';

import { knownCurrencyOr } from '@utils/format-money';
import { facilityRoute } from '@utils/trust-route';
import { arraySignal } from '@utils/signals/arraySignal';

@Component({
  selector: 'facilities',
  standalone: true,
  imports: [
    RouterModule,
    CarouselModule,
    ChartModule,
    SummaryChartComponent,
    AnalyticsLabelValuePipe,
    SelectableWidget,
  ],
  templateUrl: './facilities.component.html',
  styleUrl: './facilities.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Facilities {
  // Angular core
  private destroyRef = inject(DestroyRef);
  private viewContainerRef = inject(ViewContainerRef);

  // States
  private overviewResumeState = inject(OverviewResumeState);

  /**
   * The currency the cards format their amounts in (LEN-2465): the one the
   * loaded resume was converted to. `selection` only moves when its payload
   * lands, so the figures and their format never disagree while a new
   * currency is being fetched.
   */
  public displayCurrency = computed(() =>
    knownCurrencyOr(
      this.overviewResumeState.selection()?.currency,
      CURRENCY.MXN
    )
  );

  // Export
  exportMode = input<boolean>(false);
  selectedWidgets = input<HTMLElement[]>([]);
  widgetToggle = output<{ checked: boolean; el: HTMLElement }>();

  // Charts
  private summaryCharts = arraySignal<SummaryChart>([]);
  public carouselNumVisibleScroll = signal(3);

  exportGridCols = computed(() => {
    switch (this.carouselNumVisibleScroll()) {
      case 1:
        return 'grid grid-cols-1 gap-4';
      case 2:
        return 'grid grid-cols-2 gap-4';
      default:
        return 'grid grid-cols-3 gap-4';
    }
  });

  isWidgetSelected(el: HTMLElement): boolean {
    return this.selectedWidgets().includes(el);
  }

  summaryChartsWithNullValues = computed<(SummaryChart | null)[]>(() => {
    // what this does is grab the length of the sumarycharts and calculates how many one should be perslide
    // and if there is 5 and has to be 3 items per slide, then add one empty value to the array
    // and that shows a blank element in the carousel
    const raw = this.summaryCharts();
    const len = raw.length;
    const perSlide = this.carouselNumVisibleScroll();
    if (len <= perSlide) return raw;
    const remainder = len % perSlide;
    const blankElements = remainder === 0 ? 0 : perSlide - remainder;
    return [...raw, ...Array(blankElements).fill(null)];
  });

  operationOptions(chart: Chart.Internal.Chart) {
    let allValues: number[] = [];

    const datasets = lodash.clone(chart.data.datasets);

    datasets.forEach((dataset: any) => {
      allValues = allValues.concat(dataset.data);
    });

    const minAllValues = lodash.min(allValues);
    const maxAllValues = lodash.max(allValues);
    const stepDivisions = 5;

    // Add 10% padding to max and min
    const minYScale = lodash.floor(minAllValues! * 0.9, 1);
    let maxYScale = lodash.round(maxAllValues! * 1.1, 1);

    const diff = lodash.round(maxYScale - minYScale, 1);

    // This check prevents reprocesing
    const isDivisible = diff % (stepDivisions / 10) === 0;

    if (maxYScale - minYScale <= stepDivisions / 10) {
      maxYScale = minYScale + stepDivisions / 10;
    } else if (minYScale !== 0 && !isDivisible) {
      maxYScale =
        maxYScale + (stepDivisions - ((diff * 10) % stepDivisions)) / 10;
    }

    return {
      scales: {
        x: {
          display: false,
        },
        y: {
          display: true,
          beginAtZero: false,

          min: minYScale,

          max: maxYScale,

          ticks: {
            stepSize: () => {
              return lodash.round((maxYScale - minYScale) / stepDivisions, 2);
            },
          },
        },
      },
    };
  }

  constructor() {
    // The request lives in OverviewResumeState; this component only reads the
    // operations slice of the shared payload.
    // Reacts to the undefined the state publishes when the selection changes,
    // not just to payloads: filtering it out would leave the previous lender's
    // cards on screen while core-metrics and the banner cleared, which is the
    // mixed-state symptom of LEN-2078. An empty list clears the cards, because
    // pushSummaryCards always replaces the whole signal.
    toObservable(this.overviewResumeState.operations)
      .pipe(takeUntilDestroyed())
      .subscribe(operations => this.pushSummaryCards(operations ?? []));

    this.updateCarouselNumVisible();
    const resizeHandler = () => this.updateCarouselNumVisible();
    window.addEventListener('resize', resizeHandler);
    this.destroyRef.onDestroy(() =>
      window.removeEventListener('resize', resizeHandler)
    );
  }

  private updateCarouselNumVisible() {
    const width = window.innerWidth;
    if (width < 1100) {
      this.carouselNumVisibleScroll.set(1);
    } else if (width < 1480) {
      this.carouselNumVisibleScroll.set(2);
    } else {
      this.carouselNumVisibleScroll.set(3);
    }
  }

  pushSummaryCards(
    resumeOperations: API.Trusts.Lender.Overview.Resume.Operation[]
  ) {
    const charts = resumeOperations.map(operation => operation.charts[0]);
    const operations = resumeOperations.map(operation => {
      const clone = lodash.clone(operation);
      delete (clone as any).charts;
      return clone;
    });

    const newSummaryCharts: SummaryChart[] = [];

    operations.forEach((operation, i) => {
      const chart = charts[i];
      const chartName = chart
        ? chart.title.toUpperCase().replaceAll(' ', '_')
        : undefined;

      if (!chart) {
        const summaryChart: SummaryChart = {
          preTitle: this.getPrimaryLender(operation)?.name ?? '',
          title: operation.alias,
          subTitleQuote: `Master admin: ${operation.master_admin}`,
          countryCurrency: operation.currency,
          lastUpdated: operation.reported_at
            ? DateTime.fromISO(operation.reported_at).toJSDate()
            : undefined,
          // LEN-3164: estas tarjetas salen de `overview-resume`, que es otro
          // endpoint y NO manda `operation_id`. Siguen navegando por trust, que
          // el guard y `OperationsState` aceptan igual; cuando el trust tiene
          // dos operaciones, la que abre es la primera que matchee. Ponerle el
          // id pide agregarlo a ese payload, que es backend y no esta HU.
          route: facilityRoute(operation.trust_number),
        };

        newSummaryCharts.push(summaryChart);
        return;
      }

      const decoded = decodeChart(
        chart,
        this.destroyRef,
        this.viewContainerRef,
        {
          colors:
            chartName === API.Trusts.ChartName.OVERCOLLATERALIZATION
              ? ChartColorScheme['OVERCOLLATERALIZATION']
              : ChartColorScheme['BORROWING_BASE'],
          currency: this.displayCurrency(),
          tooltip: {
            valueIsCurrency:
              chartName === API.Trusts.ChartName.BORROWING_BASE ? true : false,
          },
        }
      ) as Chart.Internal.Chart;

      lodash.merge(decoded.options, this.operationOptions(decoded));

      const grayDashedDataset = ['Borrowing Base', 'Required'];
      const dashedBorderColor = (label: string) =>
        label === 'Required' ? COLORS.blue[900] : COLORS.primary[500];
      const datasets = decoded.data.datasets;

      decoded.data.datasets = [
        {
          ...datasets[0],
          fill: false,
          tension: 0.4,
          borderWidth: 2,
          borderDash: grayDashedDataset.includes(datasets[0].label)
            ? [5, 5]
            : undefined,
          borderColor: grayDashedDataset.includes(datasets[0].label)
            ? dashedBorderColor(datasets[0].label)
            : COLORS.secondary[500],
        },
        {
          ...datasets[1],
          fill: false,
          borderDash: undefined,
          borderColor: grayDashedDataset.includes(datasets[1].label)
            ? dashedBorderColor(datasets[1].label)
            : COLORS.secondary[500],
          tension: 0.4,
          borderWidth: 2,
        },
      ];

      let bigNumberDataset: any = undefined;
      let smallNumberDataset: any = undefined;

      switch (chartName) {
        case API.Trusts.ChartName.BORROWING_BASE:
          bigNumberDataset = datasets.find(
            dataset => dataset.label.toUpperCase() === 'OUTSTANDING DEBT'
          )!;
          smallNumberDataset = datasets.find(
            dataset => dataset.label.toUpperCase() === 'BORROWING BASE'
          )!;

          break;

        case API.Trusts.ChartName.OVERCOLLATERALIZATION:
          bigNumberDataset = datasets.find(
            dataset => dataset.label.toUpperCase() === 'REPORTED'
          )!;
          smallNumberDataset = datasets.find(
            dataset => dataset.label.toUpperCase() === 'REQUIRED'
          )!;
          break;

        default:
          break;
      }

      const summaryChart: SummaryChart = {
        preTitle: this.getPrimaryLender(operation)?.name ?? '',
        title: operation.alias,
        subTitleQuote: `Master admin: ${operation.master_admin}`,
        countryCurrency: operation.currency,
        bigNumber: {
          label:
            chartName === API.Trusts.ChartName.OVERCOLLATERALIZATION
              ? 'Reported'
              : 'Outstanding debt',
          value: lodash.last(bigNumberDataset.data)!,
          isCurrency:
            chartName === API.Trusts.ChartName.OVERCOLLATERALIZATION
              ? false
              : true,
        },
        smallNumber: {
          label:
            chartName === API.Trusts.ChartName.OVERCOLLATERALIZATION
              ? 'Required'
              : 'Borrowing base',
          value: lodash.last(smallNumberDataset.data)!,
          isCurrency:
            chartName === API.Trusts.ChartName.OVERCOLLATERALIZATION
              ? false
              : true,
        },
        lastUpdated: DateTime.fromISO(operation.reported_at).toJSDate(),
        chart: decoded,
        // LEN-3164: estas tarjetas salen de `overview-resume`, que es otro
        // endpoint y NO manda `operation_id`. Siguen navegando por trust, que
        // el guard y `OperationsState` aceptan igual; cuando el trust tiene
        // dos operaciones, la que abre es la primera que matchee. Ponerle el
        // id pide agregarlo a ese payload, que es backend y no esta HU.
        route: facilityRoute(operation.trust_number),
        status: operation.status,
      };

      newSummaryCharts.push(summaryChart);
    });

    this.summaryCharts.set(newSummaryCharts);
  }

  /**
   * El lender a mostrar para `operation`, o `undefined` si no hay ninguno.
   *
   * LEN-3121: desde LEN-3095 el backend acota `lenders` a lo que el usuario
   * puede ver, y en una facility sindicada el lender propio no es el primario
   * -- buscar sólo el primario dejaba la tarjeta sin nombre.
   *
   * Para un usuario de un lender el primero ES el suyo, porque lo que llega ya
   * viene filtrado. Para el staff de CXC no: `scope_lenders_for_user` les
   * devuelve la lista intacta, así que acá el primero es sólo el primero del
   * arreglo. Se acepta a propósito -- el caso sólo aparece en una sindicada
   * sin ningún `is_primary` marcado, y ahí mostrar un nombre es mejor que
   * mostrar la tarjeta vacía.
   *
   * El tipo de retorno va explícito: `tsconfig` no tiene
   * `noUncheckedIndexedAccess`, así que `lenders[0]` tipa como `Member` aunque
   * en runtime sea `undefined` con la lista vacía. Sin esto el compilador
   * dejaría de avisarle al próximo llamador.
   */
  private getPrimaryLender(
    operation: API.Trusts.Lender.Overview.Resume.Operation
  ):
    | API.Trusts.Lender.Overview.Resume.Operation['lenders'][number]
    | undefined {
    return (
      operation.lenders.find(lender => lender.is_primary) ??
      operation.lenders[0]
    );
  }
}
